import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { refundAdvice } from '../../lib/refundNotes';
import { chargeShares, type ChargeShare } from '../../lib/paymentGroups';
import { OutsideRefunds } from '../../components/admin/OutsideRefunds';
import { Spinner } from '../../components/design-system';
import type { Payment, PaymentAction } from '../../types';

interface PaymentRow extends Payment {
  traveler_name: string | null;
  host_name: string | null;
  experience_date: string | null;
  /** The trip is completed and the host hasn't invoiced it yet: it is ready to invoice. */
  invoiceable: boolean;
  /** The trip's last day has passed but it isn't marked completed yet. */
  completable: boolean;
  /** The host invoice this trip is on (or was paid through), if any. */
  invoice: { number: string; status: string; invoice_number: string } | null;
  /** The city of a trip with several cities; null for a trip booked as one block. */
  city: string | null;
  /** Set when other payments were taken in the same Stripe charge (one per city). */
  share: ChargeShare | null;
}

const STATUS_COLORS: Record<string, string> = {
  paid: 'bg-emerald-500/15 text-emerald-500',
  pending: 'bg-gold/15 text-gold',
  failed: 'bg-crimson/15 text-crimson',
  refunded: 'bg-app-muted/15 text-app-muted',
  refund_failed: 'bg-crimson/15 text-crimson',
};

// Hosts are paid against their invoices, so the payout column says where a trip
// stands on that. 'failed' and 'processing' only come from the old Stripe release
// and mean the same as "not invoiced yet".
const PAYOUT_COLORS: Record<string, string> = {
  paid: 'bg-emerald-500/15 text-emerald-500',
  held: 'bg-gold/15 text-gold',
  processing: 'bg-gold/15 text-gold',
  failed: 'bg-gold/15 text-gold',
  refunded: 'bg-app-muted/15 text-app-muted',
  invoiced: 'bg-violet/15 text-violet',
};

const PAYOUT_LABELS: Record<string, string> = {
  paid: 'Paid to host',
  held: 'Not invoiced yet',
  processing: 'Not invoiced yet',
  failed: 'Not invoiced yet',
  refunded: 'Refunded: no payout',
  invoiced: 'On a host invoice',
};

// The payout filter: one entry per thing an admin would look for.
const PAYOUT_FILTERS: [string, string][] = [
  ['all', 'All'],
  ['held', 'Not invoiced yet'],
  ['invoiced', 'On a host invoice'],
  ['paid', 'Paid to host'],
  ['refunded', 'Refunded: no payout'],
];
const NOT_INVOICED = ['held', 'failed', 'processing'];

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

const GRACE_DAYS = 0;

interface ConfirmState {
  actionType: 'refund';
  matchIds: string[];
  /** Only meaningful (and editable) for a single-item refund. */
  refundAmountCents?: number;
}

function ActionHistory({ paymentId }: { paymentId: string }) {
  const [actions, setActions] = useState<PaymentAction[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void supabase
      .from('payment_actions')
      .select('*')
      .eq('payment_id', paymentId)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (!cancelled) setActions((data as PaymentAction[]) ?? []);
      });
    return () => { cancelled = true; };
  }, [paymentId]);

  if (actions === null) {
    return <p className="text-xs text-app-muted py-2">Loading history…</p>;
  }
  if (actions.length === 0) {
    return <p className="text-xs text-app-muted py-2">No refunds or earlier releases recorded for this payment.</p>;
  }
  return (
    <div className="flex flex-col gap-1.5 py-2">
      {actions.map((a) => (
        <div key={a.id} className="text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={`px-1.5 py-0.5 rounded font-medium capitalize ${a.status === 'succeeded' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-crimson/15 text-crimson'}`}>
            {a.action_type} {a.status === 'succeeded' ? 'ok' : 'failed'}
          </span>
          <span className="text-app-muted">{new Date(a.created_at).toLocaleString()}</span>
          {a.amount > 0 && <span className="text-app-fg">{formatCents(a.amount, 'eur')}</span>}
          {a.stripe_object_id && <span className="text-app-muted font-mono">{a.stripe_object_id}</span>}
          {a.error_message && <span className="text-crimson">{a.error_message}</span>}
        </div>
      ))}
    </div>
  );
}

export function AdminPayments() {
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [payoutFilter, setPayoutFilter] = useState<string>('all');
  const [hostFilter, setHostFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function load() {
    const { data: rows } = await supabase
      .from('payments')
      .select('*')
      .order('created_at', { ascending: false });

    if (!rows) { setLoading(false); return; }
    const payRows = rows as Payment[];

    const userIds = [...new Set(payRows.flatMap((r) => [r.traveler_id, r.host_id]))];
    const matchIds = [...new Set(payRows.map((r) => r.match_id).filter(Boolean))] as string[];
    const portraitIds = [...new Set(payRows.map((r) => r.portrait_id).filter(Boolean))] as string[];

    const [{ data: profiles }, { data: matches }, { data: portraits }] = await Promise.all([
      supabase.from('profiles').select('id, full_name').in('id', userIds),
      matchIds.length
        ? supabase.from('matches').select('id, status, portrait_id, leg_id').in('id', matchIds)
        : Promise.resolve({ data: [] }),
      portraitIds.length
        ? supabase.from('taste_portraits').select('id, start_date, trip_days').in('id', portraitIds)
        : Promise.resolve({ data: [] }),
    ]);

    const nameMap = new Map<string, string | null>();
    (profiles ?? []).forEach((p: { id: string; full_name: string | null }) => nameMap.set(p.id, p.full_name));
    const matchMap = new Map<string, { status: string; portrait_id: string | null; leg_id: string | null }>();
    ((matches ?? []) as { id: string; status: string; portrait_id: string | null; leg_id: string | null }[]).forEach((m) => matchMap.set(m.id, m));
    const legIds = [...new Set([...matchMap.values()].map((m) => m.leg_id).filter(Boolean))] as string[];
    const { data: legRows } = legIds.length
      ? await supabase.from('trip_legs').select('id, city').in('id', legIds)
      : { data: [] };
    const cityByLeg = new Map<string, string>();
    ((legRows ?? []) as { id: string; city: string }[]).forEach((l) => cityByLeg.set(l.id, l.city));
    const portraitMap = new Map<string, { start_date: string | null; trip_days: number | null }>();
    ((portraits ?? []) as { id: string; start_date: string | null; trip_days: number | null }[]).forEach((p) => portraitMap.set(p.id, p));

    // Which host invoice each invoiced or paid trip is on.
    const invoicedIds = payRows.filter((r) => r.payout_status === 'invoiced' || r.payout_status === 'paid').map((r) => r.id);
    const { data: itemRows } = invoicedIds.length
      ? await supabase
          .from('host_invoice_items')
          .select('payment_id, released_at, host_invoices(number, status, invoice_number)')
          .in('payment_id', invoicedIds)
      : { data: [] };
    type InvoiceRef = { number: string; status: string; invoice_number: string };
    const invoiceByPayment = new Map<string, InvoiceRef>();
    ((itemRows ?? []) as unknown as { payment_id: string; released_at: string | null; host_invoices: InvoiceRef | null }[]).forEach((it) => {
      if (it.released_at === null && it.host_invoices) invoiceByPayment.set(it.payment_id, it.host_invoices);
    });

    const now = new Date();
    const cityOf = (r: Payment) => { const lid = r.match_id ? matchMap.get(r.match_id)?.leg_id : null; return lid ? cityByLeg.get(lid) ?? null : null; };
    const shares = chargeShares(payRows.map((r) => ({
      id: r.id, stripe_payment_intent_id: r.stripe_payment_intent_id, amount_cents: r.amount_cents, currency: r.currency,
      host_name: nameMap.get(r.host_id) ?? null, city: cityOf(r),
    })));
    setPayments(
      payRows.map((r) => {
        const m = r.match_id ? matchMap.get(r.match_id) : null;
        const pt = r.portrait_id ? portraitMap.get(r.portrait_id) : null;
        // Advisory signals for badges only: a trip the host can invoice now, and a
        // trip that has ended but isn't marked completed yet (completing it is
        // what makes it invoiceable).
        const stillOwed = r.status === 'paid' && NOT_INVOICED.includes(r.payout_status);
        const invoiceable = stillOwed && m?.status === 'completed';
        let completable = false;
        if (stillOwed && m?.status === 'accepted' && pt?.start_date && pt.trip_days) {
          const end = new Date(pt.start_date);
          end.setDate(end.getDate() + pt.trip_days + GRACE_DAYS);
          completable = now > end;
        }
        return {
          ...r,
          traveler_name: nameMap.get(r.traveler_id) ?? null,
          host_name: nameMap.get(r.host_id) ?? null,
          experience_date: pt?.start_date ?? null,
          invoiceable,
          completable,
          invoice: invoiceByPayment.get(r.id) ?? null,
          city: cityOf(r),
          share: shares.get(r.id) ?? null,
        };
      })
    );
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function invokeAdminPayout(body: Record<string, unknown>) {
    const { data, error } = await supabase.functions.invoke('admin-payouts', { body });
    if (error) {
      let msg = 'Payment action failed. Please try again.';
      try {
        const ctx = (error as { context?: Response } | null)?.context;
        const parsed = ctx ? await ctx.json() : null;
        if (parsed?.error) msg = parsed.error;
      } catch { /* keep generic */ }
      throw new Error(msg);
    }
    return data;
  }

  async function handleComplete() {
    setCompleting(true);
    setNotice(null);
    try {
      const res = await invokeAdminPayout({ action: 'complete' });
      const completed = res?.completed ?? 0;
      setNotice({
        kind: 'ok',
        text: completed > 0
          ? `Completed ${completed} trip${completed !== 1 ? 's' : ''}. Their hosts can now invoice ${completed !== 1 ? 'them' : 'it'}.`
          : 'No trips needed completing.',
      });
      await load();
    } catch (e) {
      setNotice({ kind: 'err', text: e instanceof Error ? e.message : 'Could not complete the finished trips.' });
    } finally {
      setCompleting(false);
    }
  }

  async function executeConfirmed() {
    if (!confirmState) return;
    const { actionType, matchIds, refundAmountCents } = confirmState;
    setActing(matchIds.join(','));
    setConfirmState(null);
    setNotice(null);
    try {
      const body: Record<string, unknown> = matchIds.length === 1
        ? { action: actionType, match_id: matchIds[0] }
        : { action: actionType, match_ids: matchIds };
      if (actionType === 'refund' && matchIds.length === 1 && refundAmountCents) {
        body.amount_cents = refundAmountCents;
      }
      const res = await invokeAdminPayout(body);

      if (matchIds.length === 1) {
        if (res?.ok && res.status === 'refunded') {
          setNotice({ kind: 'ok', text: 'Refund issued.' });
        } else if (res?.ok) {
          setNotice({ kind: 'ok', text: `No action taken — ${res.reason ?? 'already handled'}.` });
        } else {
          setNotice({ kind: 'err', text: res?.error ?? 'Refund failed.' });
        }
      } else {
        const results = (res?.results ?? []) as { ok: boolean; status: string; match_id: string }[];
        const succeeded = results.filter((r) => r.ok && r.status === 'refunded').length;
        const failed = results.length - succeeded;
        setNotice({
          kind: failed > 0 ? 'err' : 'ok',
          text: `${succeeded} of ${results.length} succeeded${failed > 0 ? `, ${failed} failed — see rows below` : ''}.`,
        });
      }
      setSelected(new Set());
      await load();
    } catch (e) {
      setNotice({ kind: 'err', text: e instanceof Error ? e.message : 'Action failed.' });
    } finally {
      setActing(null);
    }
  }

  function toggleSelected(matchId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId); else next.add(matchId);
      return next;
    });
  }

  const filtered = useMemo(() => {
    return payments.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (payoutFilter === 'held' ? !NOT_INVOICED.includes(p.payout_status) : payoutFilter !== 'all' && p.payout_status !== payoutFilter) return false;
      if (hostFilter !== 'all' && p.host_id !== hostFilter) return false;
      if (dateFrom && p.created_at < dateFrom) return false;
      if (dateTo && p.created_at > `${dateTo}T23:59:59`) return false;
      if (amountMin && p.amount_cents < Number(amountMin) * 100) return false;
      if (amountMax && p.amount_cents > Number(amountMax) * 100) return false;
      return true;
    });
  }, [payments, statusFilter, payoutFilter, hostFilter, dateFrom, dateTo, amountMin, amountMax]);

  const hostOptions = useMemo(() => {
    const map = new Map<string, string>();
    payments.forEach((p) => { if (!map.has(p.host_id)) map.set(p.host_id, p.host_name ?? 'Unknown host'); });
    return [...map.entries()];
  }, [payments]);

  const totalRevenue = payments.filter((p) => p.status === 'paid').reduce((sum, p) => sum + p.commission_cents, 0);
  const totalVolume = payments.filter((p) => p.status === 'paid').reduce((sum, p) => sum + p.amount_cents, 0);
  const vatCollected = payments.filter((p) => p.status === 'paid').reduce((sum, p) => sum + (p.vat_cents ?? 0), 0);
  // What Sekretly still owes hosts: not invoiced yet, and invoiced but not paid.
  const toInvoice = payments
    .filter((p) => p.status === 'paid' && NOT_INVOICED.includes(p.payout_status))
    .reduce((sum, p) => sum + p.host_payout_cents, 0);
  const onInvoices = payments
    .filter((p) => p.status === 'paid' && p.payout_status === 'invoiced')
    .reduce((sum, p) => sum + p.host_payout_cents, 0);
  const completableCount = payments.filter((p) => p.completable).length;

  if (loading) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl md:text-3xl font-semibold text-app-fg">Payments</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/invoices"
              className="inline-flex items-center gap-2 rounded-full border border-app-border text-app-fg text-xs font-semibold px-4 py-2 hover:bg-app-card"
            >
              Host invoices
            </Link>
            <button
              onClick={() => void handleComplete()}
              disabled={completing}
              className="inline-flex items-center gap-2 rounded-full bg-gold text-ink text-xs font-semibold px-4 py-2 disabled:opacity-60"
              title="Mark trips whose last day has passed as completed, so their hosts can invoice them. No money moves."
            >
              {completing ? <><Spinner size="sm" />Completing…</> : <>Complete finished trips{completableCount ? ` (${completableCount})` : ''}</>}
            </button>
          </div>
        </div>
        <p className="text-app-muted text-sm mb-6">
          All traveler payments. Hosts are paid against their invoices (see Host invoices); refunds to travelers are always a manual admin action.
        </p>

        <OutsideRefunds onChanged={() => void load()} />

        {notice && (
          <div className={`rounded-xl border px-4 py-2.5 mb-5 text-sm ${notice.kind === 'ok' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500' : 'border-crimson/30 bg-crimson/10 text-crimson'}`}>
            {notice.text}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
          <div className="bg-app-card rounded-xl border border-app-border p-4">
            <p className="text-xs text-app-muted uppercase tracking-wide">Total volume</p>
            <p className="text-xl font-semibold text-app-fg mt-1">{formatCents(totalVolume, 'eur')}</p>
            <p className="text-[11px] text-app-muted mt-0.5">VAT included</p>
          </div>
          <div className="bg-app-card rounded-xl border border-app-border p-4">
            <p className="text-xs text-app-muted uppercase tracking-wide">VAT collected</p>
            <p className="text-xl font-semibold text-app-fg mt-1">{formatCents(vatCollected, 'eur')}</p>
            <p className="text-[11px] text-app-muted mt-0.5">owed to the tax authority</p>
          </div>
          <div className="bg-app-card rounded-xl border border-app-border p-4">
            <p className="text-xs text-app-muted uppercase tracking-wide">Platform revenue</p>
            <p className="text-xl font-semibold text-gold mt-1">{formatCents(totalRevenue, 'eur')}</p>
            <p className="text-[11px] text-app-muted mt-0.5">without VAT, before host fees paid</p>
          </div>
          <div className="bg-app-card rounded-xl border border-app-border p-4">
            <p className="text-xs text-app-muted uppercase tracking-wide">Owed to hosts</p>
            <p className="text-xl font-semibold text-app-fg mt-1">{formatCents(toInvoice, 'eur')}</p>
            <p className="text-[11px] text-app-muted mt-0.5">not invoiced yet</p>
          </div>
          <div className="bg-app-card rounded-xl border border-app-border p-4">
            <p className="text-xs text-app-muted uppercase tracking-wide">On host invoices</p>
            <p className="text-xl font-semibold text-app-fg mt-1">{formatCents(onInvoices, 'eur')}</p>
            <p className="text-[11px] text-app-muted mt-0.5">waiting to be paid</p>
          </div>
          <div className="bg-app-card rounded-xl border border-app-border p-4">
            <p className="text-xs text-app-muted uppercase tracking-wide">Transactions</p>
            <p className="text-xl font-semibold text-app-fg mt-1">{payments.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-app-card rounded-xl border border-app-border p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-[11px] text-app-muted mb-1">Payment status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-app-bg border border-app-border rounded-lg text-xs px-2 py-1.5">
              {['all', 'pending', 'paid', 'failed', 'refunded', 'refund_failed'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="payout-filter" className="block text-[11px] text-app-muted mb-1">Host payment</label>
            <select id="payout-filter" value={payoutFilter} onChange={(e) => setPayoutFilter(e.target.value)} className="bg-app-bg border border-app-border rounded-lg text-xs px-2 py-1.5">
              {PAYOUT_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-app-muted mb-1">Host</label>
            <select value={hostFilter} onChange={(e) => setHostFilter(e.target.value)} className="bg-app-bg border border-app-border rounded-lg text-xs px-2 py-1.5 max-w-[10rem]">
              <option value="all">All hosts</option>
              {hostOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-app-muted mb-1">Booked from</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-app-bg border border-app-border rounded-lg text-xs px-2 py-1.5" />
          </div>
          <div>
            <label className="block text-[11px] text-app-muted mb-1">Booked to</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-app-bg border border-app-border rounded-lg text-xs px-2 py-1.5" />
          </div>
          <div>
            <label className="block text-[11px] text-app-muted mb-1">Amount min (€)</label>
            <input type="number" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} className="bg-app-bg border border-app-border rounded-lg text-xs px-2 py-1.5 w-20" />
          </div>
          <div>
            <label className="block text-[11px] text-app-muted mb-1">Amount max (€)</label>
            <input type="number" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} className="bg-app-bg border border-app-border rounded-lg text-xs px-2 py-1.5 w-20" />
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="bg-app-card rounded-xl border border-gold/40 p-3 mb-4 flex flex-wrap items-center gap-3">
            <span className="text-sm text-app-fg">{selected.size} selected</span>
            <button
              onClick={() => setConfirmState({ actionType: 'refund', matchIds: [...selected] })}
              disabled={acting !== null}
              className="rounded-full bg-crimson/90 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-60"
            >
              Refund selected
            </button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-app-muted hover:text-app-fg">Clear</button>
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="text-sm text-app-muted py-8 text-center">No payments found.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((p) => (
              <div key={p.id} className="bg-app-card rounded-xl border border-app-border p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <input
                    type="checkbox"
                    checked={selected.has(p.match_id)}
                    onChange={() => toggleSelected(p.match_id)}
                    className="shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-fg truncate">
                      {p.traveler_name ?? 'Unknown'} → {p.host_name ?? 'Unknown'}
                    </p>
                    <p className="text-xs text-app-muted font-mono truncate">{p.id}</p>
                    {p.share && (
                      <p className="text-xs text-app-fg">
                        {p.city && <span className="font-semibold">{p.city} · </span>}
                        one of {p.share.count} payments in the same Stripe charge ({formatCents(p.share.totalCents, p.share.currency)} in total), with{' '}
                        {p.share.others.map((o) => `${o.host ?? 'Unknown host'}${o.city ? ` (${o.city})` : ''}`).join(', ')}
                      </p>
                    )}
                    {!p.share && p.city && <p className="text-xs text-app-fg"><span className="font-semibold">{p.city}</span></p>}
                    <p className="text-xs text-app-muted">
                      {p.trip_days} day{p.trip_days > 1 ? 's' : ''} · booked {new Date(p.created_at).toLocaleDateString()}
                      {p.experience_date && <> · trip {new Date(p.experience_date).toLocaleDateString()}</>}
                      {p.status === 'paid' && <> · host fee {formatCents(p.host_payout_cents, p.currency)}</>}
                    </p>
                    {p.invoice && (
                      <p className="text-xs text-app-muted">
                        <Link to="/admin/invoices" className="text-gold underline underline-offset-2">
                          Invoice {p.invoice.number}
                        </Link>
                        {' · '}{p.invoice.invoice_number} · <span className="capitalize">{p.invoice.status}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-app-fg">{formatCents(p.amount_cents, p.currency)}</p>
                      <p className="text-xs text-app-muted">
                        {p.vat_cents != null
                          ? <>kept: {formatCents(p.commission_cents, p.currency)} · VAT {formatCents(p.vat_cents, p.currency)}</>
                          : <>fee: {formatCents(p.commission_cents, p.currency)} ({p.commission_rate}%)</>}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[p.status] ?? ''}`}>
                        {p.status.replace('_', ' ')}
                      </span>
                      {p.status === 'paid' && (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${PAYOUT_COLORS[p.payout_status] ?? ''}`}>
                          {PAYOUT_LABELS[p.payout_status] ?? p.payout_status}
                        </span>
                      )}
                      {p.invoiceable && <span className="text-[10px] text-gold">ready to invoice</span>}
                      {p.completable && <span className="text-[10px] text-app-muted">trip ended: complete it</span>}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => setConfirmState({ actionType: 'refund', matchIds: [p.match_id], refundAmountCents: p.amount_cents - p.refunded_amount_cents })}
                        disabled={acting !== null || (p.status !== 'paid' && p.status !== 'refund_failed') || p.amount_cents - p.refunded_amount_cents <= 0}
                        className="inline-flex items-center gap-1.5 rounded-full bg-crimson/90 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-40"
                      >
                        Refund
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                  className="text-[11px] text-app-muted hover:text-app-fg mt-2"
                >
                  {expanded === p.id ? 'Hide history ▲' : 'Show history ▼'}
                </button>
                {expanded === p.id && <ActionHistory paymentId={p.id} />}
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmState && (() => {
        const picked = payments.filter((p) => confirmState.matchIds.includes(p.match_id));
        const single = confirmState.matchIds.length === 1;
        const total = single && confirmState.refundAmountCents !== undefined
          ? confirmState.refundAmountCents
          : picked.reduce((n, p) => n + (p.amount_cents - p.refunded_amount_cents), 0);
        const advice = refundAdvice(picked, single ? confirmState.refundAmountCents : undefined);
        const hosts = [...new Set(picked.map((p) => p.host_name ?? 'Unknown'))].join(', ');
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div role="dialog" aria-label="Confirm refund" className="bg-app-card rounded-2xl border border-app-border p-6 max-w-md w-full">
              <h2 className="text-lg font-semibold text-app-fg mb-2">Confirm refund</h2>
              <p className="text-sm text-app-muted mb-4">
                You&apos;re about to refund {picked.length} payment{picked.length > 1 ? 's' : ''} totaling {formatCents(total, 'eur')} involving {hosts}.
              </p>
              {single && (
                <div className="mb-4">
                  <label htmlFor="refund-amount" className="block text-xs text-app-muted mb-1">Refund amount (€)</label>
                  <input
                    id="refund-amount"
                    type="number"
                    step="0.01"
                    value={((confirmState.refundAmountCents ?? 0) / 100).toFixed(2)}
                    onChange={(e) => setConfirmState({ ...confirmState, refundAmountCents: Math.round(Number(e.target.value) * 100) })}
                    className="bg-app-bg border border-app-border rounded-lg text-sm px-3 py-2 w-full"
                  />
                </div>
              )}
              {advice.notes.length > 0 && (
                <ul className={`text-xs mb-4 flex flex-col gap-1.5 ${advice.blocked ? 'text-crimson' : 'text-gold'}`}>
                  {advice.notes.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmState(null)} className="text-sm text-app-muted hover:text-app-fg px-3 py-2">Cancel</button>
                <button
                  onClick={() => void executeConfirmed()}
                  disabled={advice.blocked}
                  className="rounded-full text-sm font-medium px-4 py-2 bg-crimson text-white disabled:opacity-40"
                >
                  Confirm refund
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default AdminPayments;
